import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PhoneInternational from '../../components/PhoneInternational';
import GenderEntry from '../../components/GenderEntry';
import PersonTypeToggle from '../../components/PersonTypeToggle';
import OcupationEntry from '../../components/Ocupation';
import HowYouMeet from '../../components/HowYouMeet';
import MinimalInput from '../../components/MinimalInput/MinimalInput';
import CustomButton from '../../components/CustomButton/CustomButton';
import PasswordStrengthIndicator from '../../components/PasswordStrengthIndicator';
import { registerClientUser } from '../../redux/slices/authSlice';
import { navigateToClientDashboard } from '../../redux/slices/uiSlice';
import { useNavigate } from 'react-router-dom';
import { isValidEmail, validatePassword } from '../../utils/authUtils';

export default function ClientSignUp({ isLawyer = false }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { isLoading, error: authError } = useSelector(state => state.auth);

  const [formData, setFormData] = useState({
    nombre: '',
    apellidopaterno: '',
    apellidomaterno: '',
    correo: '',
    telefono: '',
    password: '',
    confirmPassword: '',
    personaMoral: '',
    isMoral: false,
    ocupation: '',
    fechaNacimiento: '',
    gender: '',
    formaConocernos: '',
  });
  // const [genders, setGenders] = useState([]);
  // const [occupations, setOccupations] = useState([]);
  // const [howYouMeet, setHowYouMeet] = useState([]);
  const [isDataLoaded, setIsDataLoaded] = useState(false);
  const [phoneData, setPhoneData] = useState({
    dial_code: '52',
    iso2: 'mx',
    country: 'Mexico',
  });

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (isDataLoaded) return;

      try {
        // const [gendersData, occupationsData, howYouMeetData] = await Promise.all([
        //   getGenders(),
        //   getOccupations(),
        //   getHowYouMeet(),
        // ]);

        if (isMounted) {
          // setGenders(gendersData);
          // setOccupations(occupationsData);
          // setHowYouMeet(howYouMeetData);
          setIsDataLoaded(true);
        }
      } catch (error) {
        console.error('Error fetching data:', error);
        // Could handle this with a local error state if needed, but not critical for form functionality
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [isDataLoaded]);

  const handleInputChange = useCallback(
    field => e => {
      setFormData(prev => ({
        ...prev,
        [field]: e.target.value,
      }));
    },
    []
  );

  const handleMoralChange = useCallback(value => {
    setFormData(prev => ({
      ...prev,
      isMoral: value,
    }));
  }, []);

  const handleOcupationChange = useCallback(value => {
    setFormData(prev => ({
      ...prev,
      ocupation: value,
    }));
  }, []);

  const handleGenderChange = useCallback(value => {
    setFormData(prev => ({
      ...prev,
      gender: value,
    }));
  }, []);

  const handleHowYouMeetChange = useCallback(value => {
    setFormData(prev => ({
      ...prev,
      formaConocernos: value,
    }));
  }, []);

  const handlePhoneChange = useCallback((phone, countryData) => {
    setFormData(prev => ({
      ...prev,
      telefono: phone,
    }));
    if (countryData) {
      setPhoneData({
        dial_code: countryData.dialCode || '52',
        iso2: countryData.iso2 || 'mx',
        country: countryData.name || 'Mexico',
      });
    }
  }, []);

  const validateFields = () => {
    const errors = [];

    const requiredFields = {
      nombre: 'Nombre',
      apellidopaterno: 'Apellido Paterno',
      apellidomaterno: 'Apellido Materno',
      correo: 'Correo Electrónico',
      telefono: 'Teléfono',
      password: 'Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      fechaNacimiento: 'Fecha de Nacimiento',
      gender: 'Género',
      ocupation: 'Ocupación',
      formaConocernos: 'Forma de conocernos',
    };

    // Validar campos requeridos
    for (const [field, label] of Object.entries(requiredFields)) {
      const value = formData[field];
      if (
        value === undefined ||
        value === null ||
        value === '' ||
        (typeof value === 'string' && value.trim() === '')
      ) {
        errors.push(`${label} es requerido`);
      }
    }

    // Validar persona moral
    if (formData.isMoral && (!formData.personaMoral || formData.personaMoral.trim() === '')) {
      errors.push('Nombre Persona Moral es requerido');
    }

    // Validar formato de email usando la utilidad
    if (formData.correo && !isValidEmail(formData.correo.trim())) {
      errors.push('Ingrese un correo electrónico válido');
    }

    // Validar contraseñas usando la validación robusta
    if (formData.password) {
      const passwordErrors = validatePassword(formData.password);
      errors.push(...passwordErrors);
    }

    if (formData.password !== formData.confirmPassword) {
      errors.push('Las contraseñas no coinciden');
    }

    // Validar fecha de nacimiento (mayor de 18 años)
    if (formData.fechaNacimiento) {
      const birthDate = new Date(formData.fechaNacimiento);
      const today = new Date();
      let age = today.getFullYear() - birthDate.getFullYear();
      const monthDiff = today.getMonth() - birthDate.getMonth();

      if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
        age--;
      }

      if (age < 18) {
        errors.push('Debe ser mayor de 18 años para registrarse');
      }
    }

    // Validar teléfono (básico)
    if (formData.telefono && formData.telefono.length < 10) {
      errors.push('Ingrese un número de teléfono válido');
    }

    return errors;
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (isLoading) return;

    // Validate required fields
    const validationErrors = validateFields();
    if (validationErrors.length > 0) {
      return;
    }

    try {
      const clientData = {
        client_email: formData.correo.trim(),
        client_name: formData.nombre.trim(),
        client_apellido_P: formData.apellidopaterno.trim(),
        client_apellido_M: formData.apellidomaterno.trim(),
        client_name_moral: formData.isMoral ? formData.personaMoral.trim() : null,
        client_password: formData.password,
        id_client_tipo_persona: formData.isMoral ? 1 : 0,
        client_phone_number: formData.telefono,
        phone_dialcode: phoneData.dial_code,
        phone_iso2: phoneData.iso2,
        phone_country: phoneData.country,
        client_birthday: formData.fechaNacimiento,
        id_client_gender: formData.gender,
        id_client_ocupation: formData.ocupation,
        id_client_forma_conocernos: formData.formaConocernos,
        dTimeStamp: new Date().toISOString().slice(0, 19).replace('T', ' '),
      };

      // Dispatch registration action
      await dispatch(registerClientUser(clientData)).unwrap();

      // Usar Redux para manejar la navegación
      dispatch(navigateToClientDashboard());
      // Redirect to dashboard on success
      navigate('/client-dashboard');
    } catch (error) {
      // Error handling is managed by Redux
      console.error('Registration error:', error);
    }
  };

  return (
    <form
      onSubmit={handleSubmit}
      className="grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-4 px-4 md:px-0"
    >
      <MinimalInput
        label="Nombre"
        name="nombre"
        value={formData.nombre}
        onChange={handleInputChange('nombre')}
        placeholder="Tu nombre"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <MinimalInput
        label="Apellido Paterno"
        name="apellidopaterno"
        value={formData.apellidopaterno}
        onChange={handleInputChange('apellidopaterno')}
        placeholder="Apellido paterno"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <MinimalInput
        label="Apellido Materno"
        name="apellidomaterno"
        value={formData.apellidomaterno}
        onChange={handleInputChange('apellidomaterno')}
        placeholder="Apellido materno"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <MinimalInput
        label="Correo Electrónico"
        name="correo"
        type="email"
        value={formData.correo}
        onChange={handleInputChange('correo')}
        placeholder="ejemplo@correo.com"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <div className="flex flex-col w-full mb-6">
        <label
          className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}
          htmlFor="telefono"
        >
          Teléfono
        </label>
        <PhoneInternational id="telefono" theInput={val => val} thePhone={handlePhoneChange} />
      </div>
      <div className="flex flex-col w-full mb-6">
        <MinimalInput
          label="Contraseña"
          name="password"
          type="password"
          value={formData.password}
          onChange={handleInputChange('password')}
          placeholder="••••••••"
          className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
            isLawyer
              ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
              : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
          }`}
        />
        <PasswordStrengthIndicator password={formData.password} />
      </div>
      <MinimalInput
        label="Confirmar Contraseña"
        name="confirmPassword"
        type="password"
        value={formData.confirmPassword}
        onChange={handleInputChange('confirmPassword')}
        placeholder="••••••••"
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <div className="flex flex-col w-full mb-6">
        <label className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          Género
        </label>
        <GenderEntry onGenderChange={handleGenderChange} />
      </div>
      <div className="flex flex-col w-full mb-6 items-start">
        <label className={`text-xs mb-3 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          Tipo de Persona
        </label>
        <PersonTypeToggle
          isMoral={formData.isMoral}
          onPersonTypeChange={handleMoralChange}
          isLawyer={isLawyer}
        />
      </div>
      <MinimalInput
        label="Nombre Persona Moral"
        name="personaMoral"
        value={formData.personaMoral}
        onChange={handleInputChange('personaMoral')}
        placeholder="Nombre de la persona moral"
        disabled={!formData.isMoral}
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <MinimalInput
        label="Fecha de Nacimiento"
        name="fechaNacimiento"
        type="date"
        value={formData.fechaNacimiento}
        onChange={handleInputChange('fechaNacimiento')}
        className={`rounded-md px-4 py-3 text-base transition-colors duration-200 ${
          isLawyer
            ? 'bg-transparent border border-gray-400 text-white placeholder-gray-300 focus:ring-gray-200'
            : 'bg-white border border-gray-300 text-gray-900 placeholder-gray-400 focus:ring-blue-200'
        }`}
      />
      <div className="flex flex-col w-full mb-6">
        <label className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          Ocupación
        </label>
        <OcupationEntry onOcupationChange={handleOcupationChange} />
      </div>
      <div className="flex flex-col w-full mb-6">
        <label className={`text-xs mb-1 ${isLawyer ? 'text-gray-300' : 'text-gray-500'}`}>
          ¿Cómo nos conoció?
        </label>
        <HowYouMeet onHowYouMeetChange={handleHowYouMeetChange} />
      </div>
      {authError && (
        <div className="col-span-1 md:col-span-2 text-red-500 text-xs mb-2 text-center">
          {authError}
        </div>
      )}
      <div className="col-span-1 md:col-span-2 flex justify-center mt-6">
        <CustomButton
          type="submit"
          text={isLoading ? 'Registrando...' : 'Crear Cuenta'}
          disabled={isLoading}
          className="w-full justify-center py-3 text-base"
        />
      </div>
    </form>
  );
}
